import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { SearchIcon, ChevronDownIcon, EyeIcon, FileCodeIcon, ChevronRightIcon } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { StatusBadge } from '../components/ui/StatusBadge';
import { ModalDialog } from '../components/ui/ModalDialog';
import { useIsMobile } from '../hooks/useMediaQuery';
// --- Types ---
interface Template {
  id: string;
  name: string;
  fields: string[];
}
interface Doctor {
  id: string;
  name: string;
  specialization: string;
  exams: string[];
  templates: Template[];
  status: 'Attivo' | 'Inattivo';
  phone: string;
  email: string;
  availability: string;
  machines: string[];
}
// --- Constants & Mock Data ---
const SPECIALIZATIONS = ['Neuroradiologia', 'Diagnostica per Immagini del Corpo', 'Radiologia Muscoloscheletrica', 'Senologia Diagnostica', 'Radiologia Generale', "Radiologia d'Urgenza"];
const initialDoctors: Doctor[] = [{
  id: 'DOC-001',
  name: 'Dr. Salvatore Verdi',
  specialization: 'Neuroradiologia',
  exams: ['RM Encefalo', 'RM Colonna', 'TAC Torace'],
  templates: [{
    id: 't1',
    name: 'Referto RM Encefalo Standard',
    fields: ['Indicazioni cliniche', 'Tecnica di esame', 'Reperti', 'Conclusioni']
  }, {
    id: 't2',
    name: 'Referto RM Colonna',
    fields: ['Indicazioni cliniche', 'Allineamento', 'Dischi intervertebrali', 'Midollo', 'Conclusioni']
  }],
  status: 'Attivo',
  phone: '+39 333 1234567',
  email: 's.verdi@clinica.it',
  availability: 'Lun-Ven 08:00-14:00',
  machines: ['Siemens Magnetom', 'GE Revolution']
}, {
  id: 'DOC-002',
  name: 'Dr.ssa Laura Bianchi',
  specialization: 'Senologia Diagnostica',
  exams: ['Mammografia', 'Ecografia Pelvi'],
  templates: [{
    id: 't3',
    name: 'Screening Mammografico',
    fields: ['Anamnesi', 'Seno Destro', 'Seno Sinistro', 'Cavi ascellari', 'Classificazione BIRADS']
  }],
  status: 'Attivo',
  phone: '+39 334 9876543',
  email: 'l.bianchi@clinica.it',
  availability: 'Mar-Gio 09:00-17:00',
  machines: ['Hologic Selenia', 'Philips Affiniti']
}, {
  id: 'DOC-003',
  name: 'Dr. Marco Gialli',
  specialization: 'Radiologia Muscoloscheletrica',
  exams: ['Radiografia Colonna', 'RM Colonna', 'Ecografia Addome'],
  templates: [{
    id: 't4',
    name: 'RX Colonna Completa',
    fields: ['Quesito clinico', 'Allineamento', 'Metameri', 'Spazi intersomatici', 'Conclusioni']
  }, {
    id: 't5',
    name: 'Eco Articolare',
    fields: ['Versamento', 'Tendini', 'Legamenti', 'Cartilagine', 'Conclusioni']
  }],
  status: 'Attivo',
  phone: '+39 335 4567890',
  email: 'm.gialli@clinica.it',
  availability: 'Lun-Mer-Ven 10:00-18:00',
  machines: ['Carestream DRX', 'Siemens Magnetom', 'Philips Affiniti']
}, {
  id: 'DOC-004',
  name: 'Dr.ssa Anna Neri',
  specialization: 'Diagnostica per Immagini del Corpo',
  exams: ['TAC Torace', 'TAC Addome', 'Ecografia Addome'],
  templates: [{
    id: 't6',
    name: 'TAC Addome Completo',
    fields: ['Fegato e vie biliari', 'Pancreas', 'Milza', 'Reni e surreni', 'Vescica', 'Conclusioni']
  }],
  status: 'Inattivo',
  phone: '+39 336 2345678',
  email: 'a.neri@clinica.it',
  availability: 'In congedo',
  machines: ['GE Revolution', 'Philips Affiniti']
}, {
  id: 'DOC-005',
  name: 'Dr. Giuseppe Romano',
  specialization: 'Radiologia Generale',
  exams: ['Radiografia Torace', 'Radiografia Colonna'],
  templates: [{
    id: 't7',
    name: 'RX Torace Standard',
    fields: ['Parenchima polmonare', 'Ili', 'Ombra cardiaca', 'Seni pleurici', 'Conclusioni']
  }],
  status: 'Attivo',
  phone: '+39 337 3456789',
  email: 'g.romano@clinica.it',
  availability: 'Lun-Ven 08:00-16:00',
  machines: ['Carestream DRX']
}, {
  id: 'DOC-006',
  name: 'Dr.ssa Elena Colombo',
  specialization: "Radiologia d'Urgenza",
  exams: ['TAC Torace', 'TAC Addome', 'Radiografia Torace', 'RM Encefalo'],
  templates: [{
    id: 't8',
    name: 'Referto Urgenza Trauma',
    fields: ['Dinamica', 'Cranio', 'Torace', 'Addome', 'Bacino', 'Conclusioni immediate']
  }],
  status: 'Attivo',
  phone: '+39 338 5678901',
  email: 'e.colombo@clinica.it',
  availability: 'Turni rotativi 24/7',
  machines: ['GE Revolution', 'Carestream DRX', 'Siemens Magnetom']
}, {
  id: 'DOC-007',
  name: 'Dr. Alessandro Ricci',
  specialization: 'Neuroradiologia',
  exams: ['RM Encefalo', 'TAC Torace'],
  templates: [{
    id: 't9',
    name: 'RM Encefalo con contrasto',
    fields: ['Tecnica', 'Sistema ventricolare', 'Sostanza bianca', 'Sostanza grigia', 'Enhancement', 'Conclusioni']
  }],
  status: 'Attivo',
  phone: '+39 339 6789012',
  email: 'a.ricci@clinica.it',
  availability: 'Mar-Sab 08:00-14:00',
  machines: ['Siemens Magnetom', 'GE Revolution']
}, {
  id: 'DOC-008',
  name: 'Dr.ssa Francesca Conti',
  specialization: 'Diagnostica per Immagini del Corpo',
  exams: ['Ecografia Addome', 'Ecografia Pelvi'],
  templates: [{
    id: 't10',
    name: 'Eco Pelvi Femminile',
    fields: ['Vescica', 'Utero', 'Ovaio Destro', 'Ovaio Sinistro', 'Douglas', 'Conclusioni']
  }],
  status: 'Attivo',
  phone: '+39 340 7890123',
  email: 'f.conti@clinica.it',
  availability: 'Lun-Gio 14:00-20:00',
  machines: ['Philips Affiniti']
}];
// --- Helper Functions ---
const getInitials = (name: string) => {
  const parts = name.replace('Dr.ssa ', '').replace('Dr. ', '').split(' ');
  return `${parts[0]?.[0] || ''}${parts[1]?.[0] || ''}`.toUpperCase();
};
// --- Exam Overflow Popover Component ---
function ExamOverflowPopover({
  exams,
  docId
}: {
  exams: string[];
  docId: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  const overflow = exams.length - 3;
  if (overflow <= 0) return null;
  return <div className="relative inline-flex items-center" ref={ref} data-id="element-1108">
      <button onClick={e => {
      e.stopPropagation();
      setIsOpen(!isOpen);
    }} className="px-2 py-0.5 bg-medical-green-50 text-medical-green rounded text-xs whitespace-nowrap font-medium cursor-pointer hover:bg-medical-green-100 transition-colors" data-id="element-1109">
        +{overflow}
      </button>
      <AnimatePresence data-id="element-1110">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -4
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -4
      }} transition={{
        duration: 0.15
      }} className="absolute top-full left-0 mt-2 w-64 bg-white rounded-medical-lg shadow-modal border border-clinical-gray-200 z-[60] p-3" data-id="element-1111">
            <p className="text-xs font-medium text-text-tertiary uppercase tracking-wider mb-2" data-id="element-1112">
              Tutti gli esami abilitati
            </p>
            <div className="flex flex-wrap gap-1.5" data-id="element-1113">
              {exams.map(exam => <span key={exam} className="px-2 py-1 bg-clinical-gray-100 text-text-secondary rounded text-xs" data-id="element-1114">
                  {exam}
                </span>)}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function Doctors() {
  const isMobile = useIsMobile();
  // --- State ---
  const [doctors] = useState<Doctor[]>(initialDoctors);
  // Filters
  const [searchQuery, setSearchQuery] = useState('');
  const [specFilter, setSpecFilter] = useState('Tutte');
  const [statusFilter, setStatusFilter] = useState('Tutti');
  // Modals
  type ModalType = 'detail' | 'templates' | null;
  const [activeModal, setActiveModal] = useState<ModalType>(null);
  const [selectedDoctor, setSelectedDoctor] = useState<Doctor | null>(null);
  // Template Management State
  const [expandedTemplateId, setExpandedTemplateId] = useState<string | null>(null);
  // --- Derived Data ---
  const filteredDoctors = useMemo(() => {
    return doctors.filter(doc => {
      const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase()) || doc.specialization.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSpec = specFilter === 'Tutte' || doc.specialization === specFilter;
      const matchesStatus = statusFilter === 'Tutti' || doc.status === statusFilter;
      return matchesSearch && matchesSpec && matchesStatus;
    });
  }, [doctors, searchQuery, specFilter, statusFilter]);
  // --- Handlers ---
  const handleOpenDetail = (doc: Doctor) => {
    setSelectedDoctor(doc);
    setActiveModal('detail');
  };
  const handleOpenTemplates = (doc: Doctor) => {
    setSelectedDoctor(doc);
    setExpandedTemplateId(null);
    setActiveModal('templates');
  };
  const handleExpandTemplate = (template: Template) => {
    if (expandedTemplateId === template.id) {
      setExpandedTemplateId(null);
    } else {
      setExpandedTemplateId(template.id);
    }
  };
  // --- Render Helpers ---
  const renderActions = (doc: Doctor) => <div className="flex items-center justify-end gap-1.5" data-id="element-1115">
      {isMobile ? <>
          <button onClick={e => {
        e.stopPropagation();
        handleOpenDetail(doc);
      }} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors min-h-[36px]" aria-label="Visualizza dettaglio medico" data-id="element-1116">
            <EyeIcon size={15} data-id="element-1117" /> Dettaglio
          </button>
          <button onClick={e => {
        e.stopPropagation();
        handleOpenTemplates(doc);
      }} className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors min-h-[36px]" aria-label="Visualizza modelli referto" data-id="element-1118">
            <FileCodeIcon size={15} data-id="element-1119" /> Modelli
          </button>
        </> : <>
          <div className="relative group/action" data-id="element-1120">
            <button onClick={e => {
          e.stopPropagation();
          handleOpenDetail(doc);
        }} className="p-1.5 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors" aria-label="Visualizza dettaglio medico" data-id="element-1121">
              <EyeIcon size={16} data-id="element-1122" />
            </button>
            <div className="fixed px-2 py-1 bg-text-primary text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/action:opacity-100 transition-opacity pointer-events-none shadow-lg" style={{
          zIndex: 9999
        }} ref={el => {
          if (el) {
            const btn = el.previousElementSibling as HTMLElement;
            if (btn) {
              const rect = btn.getBoundingClientRect();
              el.style.top = `${rect.top - el.offsetHeight - 6}px`;
              el.style.left = `${rect.left + rect.width / 2 - el.offsetWidth / 2}px`;
            }
          }
        }} data-id="element-1123">
              Visualizza dettaglio medico
            </div>
          </div>
          <div className="relative group/action" data-id="element-1124">
            <button onClick={e => {
          e.stopPropagation();
          handleOpenTemplates(doc);
        }} className="p-1.5 text-text-secondary hover:text-medical-green hover:bg-medical-green-50 rounded transition-colors" aria-label="Visualizza modelli referto" data-id="element-1125">
              <FileCodeIcon size={16} data-id="element-1126" />
            </button>
            <div className="fixed px-2 py-1 bg-text-primary text-white text-xs rounded whitespace-nowrap opacity-0 group-hover/action:opacity-100 transition-opacity pointer-events-none shadow-lg" style={{
          zIndex: 9999
        }} ref={el => {
          if (el) {
            const btn = el.previousElementSibling as HTMLElement;
            if (btn) {
              const rect = btn.getBoundingClientRect();
              el.style.top = `${rect.top - el.offsetHeight - 6}px`;
              el.style.left = `${rect.left + rect.width / 2 - el.offsetWidth / 2}px`;
            }
          }
        }} data-id="element-1127">
              Visualizza modelli referto
            </div>
          </div>
        </>}
    </div>;
  return <div className="space-y-6 w-full min-w-0 pb-12" data-id="element-1128">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3" data-id="element-1129">
        <div data-id="element-1130">
          <h1 className="text-2xl md:text-page-title mb-1 md:mb-2 text-text-primary font-bold" data-id="element-1131">
            Medici
          </h1>
          <p className="text-sm md:text-body text-text-secondary" data-id="element-1132">
            Anagrafica medici sincronizzata dal gestionale esterno.
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0 self-start sm:self-center" data-id="element-1133">
          <span className="text-xs text-text-tertiary" data-id="element-1134">
            Ultimo import: 11/03/2026 07:45
          </span>
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-medium bg-status-success-bg text-status-success" data-id="element-1135">
            Completato
          </span>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-3 md:items-center bg-white p-4 rounded-medical-lg shadow-sm border border-clinical-gray-100" data-id="element-1136">
        <div className="relative w-full md:w-80" data-id="element-1137">
          <SearchIcon size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-1138" />
          <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} placeholder="Cerca per nome, specializzazione..." className="medical-input pl-9 text-sm h-9 w-full" data-id="element-1139" />
        </div>
        <div className="relative w-full md:w-auto" data-id="element-1140">
          <select value={specFilter} onChange={e => setSpecFilter(e.target.value)} className="medical-input text-sm h-9 pr-8 appearance-none cursor-pointer w-full md:w-auto" data-id="element-1141">
            <option value="Tutte" data-id="element-1142">Specializzazione: Tutte</option>
            {SPECIALIZATIONS.map(spec => <option key={spec} value={spec} data-id="element-1143">
                {spec}
              </option>)}
          </select>
          <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-1144" />
        </div>
        <div className="relative w-full md:w-auto" data-id="element-1145">
          <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="medical-input text-sm h-9 pr-8 appearance-none cursor-pointer w-full md:w-auto" data-id="element-1146">
            <option value="Tutti" data-id="element-1147">Stato: Tutti</option>
            <option value="Attivo" data-id="element-1148">Attivo</option>
            <option value="Inattivo" data-id="element-1149">Inattivo</option>
          </select>
          <ChevronDownIcon size={14} className="absolute right-3 top-1/2 -translate-y-1/2 text-text-tertiary pointer-events-none" data-id="element-1150" />
        </div>
      </div>

      {/* Table / Cards */}
      {!isMobile ? <div className="w-full max-w-full bg-white rounded-medical-lg shadow-card border border-clinical-gray-100" style={{
      overflowX: 'auto',
      overflowY: 'visible'
    }} data-id="element-1151">
          <table className="w-full text-left border-collapse" style={{
        tableLayout: 'fixed',
        minWidth: '900px'
      }} data-id="element-1152">
            <colgroup data-id="element-1153">
              <col style={{
            width: '72px',
            minWidth: '72px'
          }} data-id="element-1154" />
              <col style={{
            width: '20%'
          }} data-id="element-1155" />
              <col style={{
            width: '18%'
          }} data-id="element-1156" />
              <col style={{
            width: '26%'
          }} data-id="element-1157" />
              <col style={{
            width: '9%'
          }} data-id="element-1158" />
              <col style={{
            width: '19%'
          }} data-id="element-1159" />
            </colgroup>
            <thead data-id="element-1160">
              <tr className="bg-clinical-gray border-b border-clinical-gray-200" data-id="element-1161">
                <th className="py-3 text-center text-text-secondary whitespace-nowrap pl-[0px] pr-[0px]" data-id="element-1162">
                  Foto
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-1163">
                  Nome completo
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-1164">
                  Specializzazioni
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap" data-id="element-1165">
                  Esami abilitati
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap text-center" data-id="element-1166">
                  Modelli
                </th>
                <th className="px-4 py-3 text-table-header text-text-secondary whitespace-nowrap text-right" data-id="element-1167">
                  Azioni
                </th>
              </tr>
            </thead>
            <tbody data-id="element-1168">
              {filteredDoctors.length === 0 ? <tr data-id="element-1169">
                  <td colSpan={6} className="px-4 py-8 text-center text-text-secondary text-body" data-id="element-1170">
                    Nessun medico trovato con i filtri correnti.
                  </td>
                </tr> : filteredDoctors.map(doc => <tr key={doc.id} className="border-b border-clinical-gray-100 last:border-0 hover:bg-clinical-gray/30 transition-colors" data-id="element-1171">
                    <td className="py-3 pl-[0px] pr-[0px]" data-id="element-1172">
                      <div className="flex justify-center" data-id="element-1173">
                        <div className="w-10 h-10 rounded-full bg-medical-green text-white flex items-center justify-center text-sm font-medium shrink-0 relative" data-id="element-1174">
                          {getInitials(doc.name)}
                          <div className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${doc.status === 'Attivo' ? 'bg-status-success' : 'bg-clinical-gray-300'}`} data-id="element-1175" />
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-body text-text-primary font-medium truncate" data-id="element-1176">
                      {doc.name}
                    </td>
                    <td className="px-4 py-3 text-body text-text-secondary truncate" data-id="element-1177">
                      {doc.specialization}
                    </td>
                    <td className="px-4 py-3" data-id="element-1178">
                      <div className="flex flex-wrap gap-1.5 items-center" data-id="element-1179">
                        {doc.exams.slice(0, 3).map(exam => <span key={exam} className="px-2 py-0.5 bg-clinical-gray-100 text-text-secondary rounded text-xs whitespace-nowrap" data-id="element-1180">
                            {exam}
                          </span>)}
                        <ExamOverflowPopover exams={doc.exams} docId={doc.id} data-id="element-1181" />
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center" data-id="element-1182">
                      <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-medical-green-50 text-medical-green text-xs font-medium" data-id="element-1183">
                        {doc.templates.length}
                      </span>
                    </td>
                    <td className="px-4 py-3" data-id="element-1184">
                      <div className="flex justify-end" data-id="element-1185">
                        {renderActions(doc)}
                      </div>
                    </td>
                  </tr>)}
            </tbody>
          </table>
        </div> : <div className="space-y-3" data-id="element-1186">
          {filteredDoctors.length === 0 ? <div className="p-6 text-center text-text-secondary text-body bg-white rounded-medical-lg shadow-card border border-clinical-gray-100" data-id="element-1187">
              Nessun medico trovato con i filtri correnti.
            </div> : filteredDoctors.map(doc => <div key={doc.id} className="bg-white rounded-medical-lg shadow-card border border-clinical-gray-100 p-4 space-y-4" data-id="element-1188">
                <div className="flex items-start gap-3" data-id="element-1189">
                  <div className="w-12 h-12 rounded-full bg-medical-green text-white flex items-center justify-center text-base font-medium shrink-0 relative" data-id="element-1190">
                    {getInitials(doc.name)}
                    <div className={`absolute bottom-0 right-0 w-3.5 h-3.5 rounded-full border-2 border-white ${doc.status === 'Attivo' ? 'bg-status-success' : 'bg-clinical-gray-300'}`} data-id="element-1191" />
                  </div>
                  <div className="min-w-0 flex-1" data-id="element-1192">
                    <h3 className="text-sm font-semibold text-text-primary truncate" data-id="element-1193">
                      {doc.name}
                    </h3>
                    <p className="text-xs text-text-secondary truncate mt-0.5" data-id="element-1194">
                      {doc.specialization}
                    </p>
                    <div className="mt-2 flex flex-wrap gap-1 items-center" data-id="element-1195">
                      {doc.exams.slice(0, 3).map(exam => <span key={exam} className="px-2 py-0.5 bg-clinical-gray-100 text-text-secondary rounded text-xs" data-id="element-1196">
                          {exam}
                        </span>)}
                      <ExamOverflowPopover exams={doc.exams} docId={doc.id} data-id="element-1197" />
                    </div>
                  </div>
                </div>
                <div className="flex items-center justify-between pt-3 border-t border-clinical-gray-100" data-id="element-1198">
                  <div className="text-xs text-text-secondary" data-id="element-1199">
                    <span className="font-medium text-text-primary" data-id="element-1200">
                      {doc.templates.length}
                    </span>{' '}
                    modelli referto
                  </div>
                  {renderActions(doc)}
                </div>
              </div>)}
        </div>}

      {/* --- Modals --- */}

      {/* Detail Modal */}
      <ModalDialog isOpen={activeModal === 'detail'} onClose={() => setActiveModal(null)} title="Dettaglio Medico" maxWidth="max-w-2xl" footer={<button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setActiveModal(null)} data-id="element-1202">
            Chiudi
          </button>} data-id="element-1201">
        {selectedDoctor && <div className="space-y-6" data-id="element-1203">
            <div className="flex items-center gap-4 pb-6 border-b border-clinical-gray-100" data-id="element-1204">
              <div className="w-20 h-20 rounded-full bg-medical-green text-white flex items-center justify-center text-2xl font-medium shrink-0" data-id="element-1205">
                {getInitials(selectedDoctor.name)}
              </div>
              <div data-id="element-1206">
                <h2 className="text-xl font-bold text-text-primary" data-id="element-1207">
                  {selectedDoctor.name}
                </h2>
                <p className="text-text-secondary mb-2" data-id="element-1208">
                  {selectedDoctor.specialization}
                </p>
                <StatusBadge status={selectedDoctor.status === 'Attivo' ? 'Connesso' : 'Disconnesso'} data-id="element-1209" />
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6" data-id="element-1210">
              <div className="space-y-4" data-id="element-1211">
                <div data-id="element-1212">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1213">
                    Contatti telefonici
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-1214">
                    {selectedDoctor.phone || 'Non specificato'}
                  </span>
                </div>
                <div data-id="element-1215">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1216">
                    Email
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-1217">
                    {selectedDoctor.email || 'Non specificata'}
                  </span>
                </div>
                <div data-id="element-1218">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-1" data-id="element-1219">
                    Reperibilità oraria
                  </span>
                  <span className="text-sm font-medium text-text-primary" data-id="element-1220">
                    {selectedDoctor.availability || 'Non specificata'}
                  </span>
                </div>
              </div>
              <div className="space-y-4" data-id="element-1221">
                <div data-id="element-1222">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-2" data-id="element-1223">
                    Esami abilitati
                  </span>
                  <div className="flex flex-wrap gap-1.5" data-id="element-1224">
                    {selectedDoctor.exams.map(exam => <span key={exam} className="px-2 py-1 bg-clinical-gray-100 text-text-secondary rounded text-xs" data-id="element-1225">
                        {exam}
                      </span>)}
                    {selectedDoctor.exams.length === 0 && <span className="text-sm text-text-tertiary" data-id="element-1226">
                        Nessun esame
                      </span>}
                  </div>
                </div>
                <div data-id="element-1227">
                  <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-2" data-id="element-1228">
                    Macchinari operabili
                  </span>
                  <div className="flex flex-wrap gap-1.5" data-id="element-1229">
                    {selectedDoctor.machines.map(machine => <span key={machine} className="px-2 py-1 bg-medical-green-50 text-medical-green rounded text-xs" data-id="element-1230">
                        {machine}
                      </span>)}
                    {selectedDoctor.machines.length === 0 && <span className="text-sm text-text-tertiary" data-id="element-1231">
                        Nessun macchinario
                      </span>}
                  </div>
                </div>
              </div>
            </div>

            <div data-id="element-1232">
              <span className="text-xs text-text-tertiary uppercase tracking-wider block mb-2" data-id="element-1233">
                Elenco modelli di referto compilabili
              </span>
              <div className="bg-clinical-gray-50 rounded-medical border border-clinical-gray-200 divide-y divide-clinical-gray-200" data-id="element-1234">
                {selectedDoctor.templates.map(template => <div key={template.id} className="p-3" data-id="element-1235">
                    <p className="text-sm font-medium text-text-primary" data-id="element-1236">
                      {template.name}
                    </p>
                    <p className="text-xs text-text-secondary mt-1 truncate" data-id="element-1237">
                      Campi: {template.fields.join(', ')}
                    </p>
                  </div>)}
                {selectedDoctor.templates.length === 0 && <div className="p-4 text-center text-sm text-text-secondary" data-id="element-1238">
                    Nessun modello configurato
                  </div>}
              </div>
            </div>
          </div>}
      </ModalDialog>

      {/* Template Management Modal (Read Only) */}
      <ModalDialog isOpen={activeModal === 'templates'} onClose={() => setActiveModal(null)} title={`Modelli Referto - ${selectedDoctor?.name || ''}`} maxWidth="max-w-3xl" footer={<button className="medical-btn-secondary w-full sm:w-auto" onClick={() => setActiveModal(null)} data-id="element-1240">
            Chiudi
          </button>} data-id="element-1239">
        {selectedDoctor && <div className="space-y-4" data-id="element-1241">
            <div className="flex justify-between items-center mb-4" data-id="element-1242">
              <p className="text-sm text-text-secondary" data-id="element-1243">
                Visualizza i modelli di referto associati a questo medico.
              </p>
            </div>

            <div className="space-y-3" data-id="element-1244">
              {selectedDoctor.templates.map(template => {
            const isExpanded = expandedTemplateId === template.id;
            return <div key={template.id} className={`border rounded-medical-lg overflow-hidden transition-colors ${isExpanded ? 'border-medical-green ring-1 ring-medical-green' : 'border-clinical-gray-200 hover:border-medical-green/50'}`} data-id="element-1245">
                    <div className={`flex items-center justify-between p-4 cursor-pointer ${isExpanded ? 'bg-medical-green-50' : 'bg-white'}`} onClick={() => handleExpandTemplate(template)} data-id="element-1246">
                      <div className="flex items-center gap-3" data-id="element-1247">
                        <ChevronRightIcon size={18} className={`text-text-tertiary transition-transform ${isExpanded ? 'rotate-90 text-medical-green' : ''}`} data-id="element-1248" />
                        <span className={`font-medium ${isExpanded ? 'text-medical-green' : 'text-text-primary'}`} data-id="element-1249">
                          {template.name}
                        </span>
                      </div>
                      <span className="text-xs text-text-tertiary" data-id="element-1250">
                        {template.fields.length} campi
                      </span>
                    </div>

                    <AnimatePresence data-id="element-1251">
                      {isExpanded && <motion.div initial={{
                  height: 0,
                  opacity: 0
                }} animate={{
                  height: 'auto',
                  opacity: 1
                }} exit={{
                  height: 0,
                  opacity: 0
                }} className="border-t border-medical-green/20 bg-white p-4 space-y-4" data-id="element-1252">
                          <div data-id="element-1253">
                            <label className="block text-sm font-medium text-text-primary mb-1.5" data-id="element-1254">
                              Campi del referto
                            </label>
                            <ul className="list-disc list-inside text-sm text-text-secondary space-y-1" data-id="element-1255">
                              {template.fields.map((field, idx) => <li key={idx} data-id="element-1256">{field}</li>)}
                            </ul>
                          </div>
                        </motion.div>}
                    </AnimatePresence>
                  </div>;
          })}

              {selectedDoctor.templates.length === 0 && <div className="p-8 text-center bg-clinical-gray-50 rounded-medical-lg border border-dashed border-clinical-gray-300" data-id="element-1257">
                  <FileCodeIcon size={32} className="mx-auto text-text-tertiary mb-3" data-id="element-1258" />
                  <p className="text-sm text-text-secondary" data-id="element-1259">
                    Nessun modello di referto configurato per questo medico.
                  </p>
                </div>}
            </div>
          </div>}
      </ModalDialog>
    </div>;
}